import { DatasetMetadataType } from '@routes/data-qa/datasets/dataset-metadata-type'
import {
  type BehaviorOptions,
  BehaviorType,
  BiasType,
  CustomUtility,
  getBehaviorByType,
  HuggingFaceDataset,
  matchBehavior,
  RunInfo,
  UnlearningLlmAdvancedOptions,
  type UnlearnType,
} from '@routes/llm/runs/llm-run.svelte'
import { HirundoCSV } from '@utils/hirundo-csv.svelte'

type SerializedHuggingFaceDataset = Omit<
  ReturnType<HuggingFaceDataset['toJSON']>,
  'saveToken' | 'tokenName'
> & {
  saveToken?: boolean
  tokenName?: string
}
type SerializedDataset = SerializedHuggingFaceDataset | ReturnType<HirundoCSV['toJSON']>
type SerializedCustomBehavior = Omit<
  Extract<BehaviorOptions, { type: BehaviorType.CUSTOM }>,
  'biasedDataset' | 'unbiasedDataset'
> & {
  biasedDataset: SerializedDataset
  unbiasedDataset: SerializedDataset
}
type SerializedBehavior =
  | Exclude<BehaviorOptions, { type: BehaviorType.CUSTOM }>
  | SerializedCustomBehavior
type SerializedRunInfo = {
  organizationId: number
  targetBehaviors: SerializedBehavior[]
  targetUtilities: Array<{ dataset?: SerializedDataset }>
  advancedOptions: ReturnType<UnlearningLlmAdvancedOptions['toJSON']> | null
  aggressiveness: number | null
}

type RunLlmRestoreState = {
  unlearnType: UnlearnType | null
  multipleUnlearnTypes: boolean
  runInfo: SerializedRunInfo
}

const isRunLlmRestoreState = (value: unknown): value is RunLlmRestoreState =>
  !!value && typeof value === 'object' && 'runInfo' in (value as Record<string, unknown>)

const createRunLlmRestoreState = ({
  unlearnType,
  multipleUnlearnTypes,
  runInfo,
}: {
  unlearnType: UnlearnType | null
  multipleUnlearnTypes: boolean
  runInfo: RunInfo
}) =>
  ({
    unlearnType,
    multipleUnlearnTypes,
    runInfo: runInfo.toJSON(),
  }) satisfies RunLlmRestoreState

const hydrateDataset = (
  dataset: NonNullable<RunLlmRestoreState['runInfo']['targetUtilities'][number]['dataset']>,
) => {
  if (dataset.type === DatasetMetadataType.HirundoCSV) {
    return new HirundoCSV(dataset.csvUrl)
  }

  return new HuggingFaceDataset(dataset.huggingFaceDatasetName, {
    token: dataset.token,
    tokenId: dataset.tokenId,
    saveToken: 'saveToken' in dataset ? dataset.saveToken : false,
    tokenName: 'tokenName' in dataset ? dataset.tokenName : '',
  })
}

const hydrateBehavior = (
  behavior: RunLlmRestoreState['runInfo']['targetBehaviors'][number],
  enabledBehaviors: string[],
) => {
  if (!enabledBehaviors.includes(behavior.type)) {
    const fallback = enabledBehaviors[0]
    if (!fallback) {
      return null
    }
    return getBehaviorByType(fallback as BehaviorType, { biasType: BiasType.ALL })
  }
  return matchBehavior<SerializedBehavior, BehaviorOptions>(behavior, {
    [BehaviorType.BIAS]: (matchedBehavior) =>
      getBehaviorByType(BehaviorType.BIAS, { biasType: matchedBehavior.biasType }),
    [BehaviorType.HALLUCINATION]: (matchedBehavior) =>
      getBehaviorByType(BehaviorType.HALLUCINATION, {
        hallucinationType: matchedBehavior.hallucinationType,
      }),
    [BehaviorType.SECURITY]: () => getBehaviorByType(BehaviorType.SECURITY),
    [BehaviorType.REFUSAL]: () => getBehaviorByType(BehaviorType.REFUSAL),
    [BehaviorType.CUSTOM]: (matchedBehavior) => {
      const customBehavior = getBehaviorByType(BehaviorType.CUSTOM)
      customBehavior.biasedDataset = hydrateDataset(matchedBehavior.biasedDataset)
      customBehavior.unbiasedDataset = hydrateDataset(matchedBehavior.unbiasedDataset)
      return customBehavior
    },
  })
}

const restoreRunLlmState = (restoreState: RunLlmRestoreState, enabledBehaviors: string[]) => {
  const restoredRunInfo = new RunInfo(restoreState.runInfo.organizationId)
  restoredRunInfo.targetBehaviors = restoreState.runInfo.targetBehaviors.flatMap((behavior) => {
    const hydrated = hydrateBehavior(behavior, enabledBehaviors)
    return hydrated ? [hydrated] : []
  })
  restoredRunInfo.targetUtilities = restoreState.runInfo.targetUtilities.map(
    (utility) => new CustomUtility(utility.dataset ? hydrateDataset(utility.dataset) : undefined),
  )
  restoredRunInfo.advancedOptions = UnlearningLlmAdvancedOptions.fromJSON(
    restoreState.runInfo.advancedOptions,
  )
  restoredRunInfo.aggressiveness = restoreState.runInfo.aggressiveness

  return {
    warning: restoreState.runInfo.targetBehaviors.some(
      (behavior) => !enabledBehaviors.includes(behavior.type),
    )
      ? 'The saved behavior is disabled on this deployment. Review the selection and settings before starting a run.'
      : null,
    unlearnType: restoreState.unlearnType,
    multipleUnlearnTypes: restoreState.multipleUnlearnTypes,
    runInfo: restoredRunInfo,
  }
}

export {
  createRunLlmRestoreState,
  isRunLlmRestoreState,
  restoreRunLlmState,
  type RunLlmRestoreState,
}
